import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, X } from "lucide-react";

import { HubDataTable, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { HubWorkspaceBody, HubWorkspaceHeader, HubWorkspacePage } from "@/components/layout/HubWorkspace";
import { Button } from "@/components/ui/Button";
import { hubModalOverlayProps } from "@/components/ui/HubModalOverlay";
import { Label } from "@/components/ui/Input";
import {
  deferredCommitDirtyClassName,
  deferredCommitFlashClassName,
  useDeferredCommitFieldFeedback,
} from "@/components/mrp/deferredCommitFieldShared";
import { Input } from "@/components/ui/Input";
import { useHubToast } from "@/components/ui/HubToast";
import { SearchableMultiSelect } from "@/components/ui/SearchableMultiSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { HoverHintContent, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError, apiFetch } from "@/lib/api";
import { hubModalBodyScrollClassName, hubModalShellClassName } from "@/lib/hubLayoutClassNames";
import { cn } from "@/lib/utils";
import { useHubModuleAdmin } from "@/lib/useHubModuleAdmin";

type ScriptLink = { id: number; label: string };

type OperatorScript = ScriptLink & { steps?: string; prompts?: string[] };

type FailureModeRow = {
  id: number;
  category: string;
  name: string;
  description: string;
  ncrCount: number;
  operatorScripts: ScriptLink[];
  scripts: ScriptLink[];
};

type FailureModeList = {
  operatorScripts: OperatorScript[];
  scriptOptions: OperatorScript[];
  rows: FailureModeRow[];
};

const longTextClassName =
  "min-h-24 w-full min-w-64 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-slate-100";

function useFieldSaveFlash(feedbackId: string) {
  const [commitSucceeded, setCommitSucceeded] = useState(false);
  const [commitError, setCommitError] = useState<string | null>(null);
  const flashTone = useDeferredCommitFieldFeedback(commitSucceeded, commitError, {
    playAudio: false,
    feedbackId,
  });
  async function run(save: () => Promise<unknown>) {
    setCommitSucceeded(false);
    setCommitError(null);
    try {
      await save();
      setCommitSucceeded(true);
    } catch (error) {
      setCommitError(error instanceof ApiError ? error.message : "Could not save.");
    }
  }
  return { flashTone, run };
}

async function commitOnBlur(
  draft: string,
  value: string,
  onCommit: (next: string) => Promise<unknown>,
  reset: (value: string) => void,
) {
  const next = draft.trim();
  if (!next || next === value) {
    reset(value);
    return;
  }
  await onCommit(next);
}

function FailureModeShortText({
  value,
  label,
  feedbackId,
  disabled,
  onCommit,
}: {
  value: string;
  label: string;
  feedbackId: string;
  disabled: boolean;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(value);
  const { flashTone, run } = useFieldSaveFlash(feedbackId);
  useEffect(() => setDraft(value), [value]);
  return (
    <Input
      aria-label={label}
      className={cn(
        "w-full",
        deferredCommitDirtyClassName(draft.trim() !== value, disabled),
        deferredCommitFlashClassName(flashTone),
      )}
      value={draft}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commitOnBlur(draft, value, (next) => run(() => onCommit(next)), setDraft)}
    />
  );
}

function FailureModeLongText({
  value,
  label,
  feedbackId,
  disabled,
  onCommit,
}: {
  value: string;
  label: string;
  feedbackId: string;
  disabled: boolean;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const [draft, setDraft] = useState(value);
  const { flashTone, run } = useFieldSaveFlash(feedbackId);
  useEffect(() => setDraft(value), [value]);
  return (
    <div className="flex h-full min-h-24 flex-col">
    <textarea
      aria-label={label}
      className={cn(
        longTextClassName,
        "h-full !min-h-0 flex-1",
        deferredCommitDirtyClassName(draft.trim() !== value, disabled),
        deferredCommitFlashClassName(flashTone),
      )}
      value={draft}
      disabled={disabled}
      onClick={(event) => event.stopPropagation()}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={() => void commitOnBlur(draft, value, (next) => run(() => onCommit(next)), setDraft)}
    />
    </div>
  );
}

function FailureModeCategorySelect({
  value,
  categories,
  disabled,
  feedbackId,
  onCommit,
}: {
  value: string;
  categories: string[];
  disabled: boolean;
  feedbackId: string;
  onCommit: (next: string) => Promise<unknown>;
}) {
  const [search, setSearch] = useState("");
  const { flashTone, run } = useFieldSaveFlash(feedbackId);
  const options = useMemo(() => {
    const names = new Set(categories);
    if (value.trim()) names.add(value);
    return [...names].sort((left, right) => left.localeCompare(right)).map((name) => ({ value: name, label: name }));
  }, [categories, value]);
  return (
    <div className={cn("min-w-40 rounded-md", deferredCommitFlashClassName(flashTone))} onClick={(event) => event.stopPropagation()}>
      <SearchableSelect
        value={value}
        options={options}
        searchValue={search}
        onSearchChange={setSearch}
        disabled={disabled}
        placeholder="Category"
        searchPlaceholder="Search categories"
        emptyMessage="No categories"
        onValueChange={(option) => {
          const next = option?.value.trim() ?? "";
          if (!next || next === value) return;
          void run(() => onCommit(next));
        }}
      />
    </div>
  );
}

function scriptPromptPeek(script: { steps?: string; prompts?: string[] } | undefined) {
  const steps = script?.steps?.trim() ?? "";
  if (steps !== "") return <p className="whitespace-pre-wrap">{steps}</p>;
  const prompts = script?.prompts ?? [];
  if (prompts.length === 0) return "No prompts.";
  return (
    <ol className="list-decimal space-y-1 pl-4">
      {prompts.map((prompt, index) => (
        <li key={`${index}-${prompt}`}>{prompt}</li>
      ))}
    </ol>
  );
}

function FailureModeScriptSelect({
  linked,
  options,
  disabled,
  addLabel,
  searchPlaceholder,
  emptyMessage,
  onChange,
}: {
  linked: ScriptLink[];
  options: OperatorScript[];
  disabled: boolean;
  addLabel: string;
  searchPlaceholder: string;
  emptyMessage: string;
  onChange: (scriptIds: number[]) => void;
}) {
  const [search, setSearch] = useState("");
  const selectedValues = useMemo(
    () => new Set(linked.map((script) => String(script.id))),
    [linked],
  );
  const selectOptions = useMemo(() => {
    const labels = new Map(options.map((option) => [String(option.id), option.label]));
    for (const script of linked) labels.set(String(script.id), script.label);
    return [...labels.entries()].map(([value, label]) => ({ value, label }));
  }, [options, linked]);
  const scriptsById = useMemo(() => new Map(options.map((option) => [String(option.id), option])), [options]);

  return (
    <div className="min-w-0" onClick={(event) => event.stopPropagation()}>
      <SearchableMultiSelect
        variant="chips"
        alwaysShowChips
        className="min-w-0"
        triggerClassName="!min-w-0 !flex-none !basis-full"
        addLabel={addLabel}
        renderChip={({ option, disabled: chipDisabled, onRemove }) => (
          <span className="flex w-full min-w-0 items-start gap-1 rounded-md bg-slate-100 px-2 py-0.5 text-xs text-slate-800">
            <HoverHintContent
              block
              hoverHint={scriptPromptPeek(scriptsById.get(option.value))}
              className="min-w-0 flex-1 whitespace-normal break-words"
            >
              {option.label}
            </HoverHintContent>
            <button
              type="button"
              className="rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-slate-800 disabled:pointer-events-none"
              aria-label={`Remove ${option.label}`}
              disabled={chipDisabled}
              onClick={(event) => {
                event.stopPropagation();
                onRemove();
              }}
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          </span>
        )}
        placeholder={addLabel}
        searchPlaceholder={searchPlaceholder}
        emptyMessage={emptyMessage}
        selectedValues={selectedValues}
        onSelectedValuesChange={(next) => {
          onChange(
            [...next]
              .map((value) => Number(value))
              .filter((value) => Number.isInteger(value) && value > 0),
          );
        }}
        options={selectOptions}
        searchValue={search}
        onSearchChange={setSearch}
        disabled={disabled}
      />
    </div>
  );
}

function CreateFailureModeModal({
  open,
  pending,
  categories,
  onClose,
  onCreate,
}: {
  open: boolean;
  pending: boolean;
  categories: string[];
  onClose: () => void;
  onCreate: (input: { category: string; name: string; description: string }) => void;
}) {
  const [selectedCategory, setSelectedCategory] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (!open) return;
    setSelectedCategory("");
    setNewCategory("");
    setCategorySearch("");
    setName("");
    setDescription("");
  }, [open]);

  if (!open) return null;

  const typedCategory = newCategory.trim();
  const trimmedCategory = typedCategory !== "" ? typedCategory : selectedCategory.trim();
  const trimmedName = name.trim();
  const trimmedDescription = description.trim();
  const canSubmit = !pending && trimmedCategory !== "" && trimmedName !== "" && trimmedDescription !== "";
  const categoryOptions = categories.map((value) => ({ value, label: value }));

  return createPortal(
    <div {...hubModalOverlayProps(true)}>
      <div
        className={hubModalShellClassName("form")}
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-failure-mode-title"
      >
        <div className="border-b border-slate-200 px-4 py-3">
          <h2 id="create-failure-mode-title" className="text-base font-semibold text-slate-900">
            New failure mode
          </h2>
        </div>
        <form
          className={`${hubModalBodyScrollClassName()} space-y-3 px-4 py-4`}
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            onCreate({ category: trimmedCategory, name: trimmedName, description: trimmedDescription });
          }}
        >
          <div>
            <Label htmlFor="create-failure-mode-category">Category</Label>
            <div className="mt-1.5 space-y-2">
              {categoryOptions.length > 0 ? (
                <SearchableSelect
                  value={categories.includes(selectedCategory) ? selectedCategory : ""}
                  options={categoryOptions}
                  searchValue={categorySearch}
                  onSearchChange={setCategorySearch}
                  placeholder="Choose an existing category"
                  searchPlaceholder="Search categories"
                  emptyMessage="No categories"
                  onValueChange={(option) => {
                    setSelectedCategory(option?.value ?? "");
                    setNewCategory("");
                    setCategorySearch("");
                  }}
                />
              ) : null}
              <Input
                id="create-failure-mode-category"
                value={newCategory}
                placeholder="Or type a new category"
                onChange={(event) => setNewCategory(event.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="create-failure-mode-name">Failure mode</Label>
            <Input
              id="create-failure-mode-name"
              className="mt-1.5"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="create-failure-mode-description">Description</Label>
            <textarea
              id="create-failure-mode-description"
              className={`${longTextClassName} mt-1.5`}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 border-t border-slate-200 pt-3">
            <Button type="button" variant="secondary" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              Add failure mode
            </Button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}

export function QualityFailureModesPage() {
  const canEdit = useHubModuleAdmin("quality");
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const [createOpen, setCreateOpen] = useState(false);
  const list = useQuery({
    queryKey: ["quality", "failure-modes"],
    queryFn: () => apiFetch<FailureModeList>("/api/quality/failure-modes"),
  });

  const createMode = useMutation({
    mutationFn: (input: { category: string; name: string; description: string }) =>
      apiFetch("/api/quality/failure-modes", { method: "POST", body: input }),
    onSuccess: () => {
      setCreateOpen(false);
      showToast({ tone: "success", message: "Failure mode added." });
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
    },
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not add the failure mode.",
      });
    },
  });

  const saveDetails = useMutation({
    mutationFn: (input: { id: number; category?: string; name?: string; description?: string }) =>
      apiFetch(`/api/quality/failure-modes/${input.id}`, {
        method: "PATCH",
        body: {
          ...(input.category != null ? { category: input.category } : {}),
          ...(input.name != null ? { name: input.name } : {}),
          ...(input.description != null ? { description: input.description } : {}),
        },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] }),
    onError: (error: unknown) => {
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "Could not save the failure mode.",
      });
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
    },
  });

  const saveOperatorLinks = useMutation({
    mutationFn: (input: { id: number; scriptIds: number[] }) =>
      apiFetch(`/api/quality/failure-modes/${input.id}/operator-scripts`, {
        method: "PUT",
        body: { scriptIds: input.scriptIds },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] }),
    onError: (error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Could not save the operator script link." });
    },
  });

  const saveScripts = useMutation({
    mutationFn: (input: { id: number; scriptIds: number[] }) =>
      apiFetch(`/api/quality/failure-modes/${input.id}/scripts`, {
        method: "PUT",
        body: { scriptIds: input.scriptIds },
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] }),
    onError: (error: unknown) => {
      showToast({ tone: "error", message: error instanceof ApiError ? error.message : "Could not save the script link." });
    },
  });

  const removeMode = useMutation({
    mutationFn: (id: number) => apiFetch(`/api/quality/failure-modes/${id}`, { method: "DELETE" }),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ["quality", "failure-modes"] });
      const previous = queryClient.getQueryData<FailureModeList>(["quality", "failure-modes"]);
      queryClient.setQueryData<FailureModeList>(["quality", "failure-modes"], (current) =>
        current == null ? current : { ...current, rows: current.rows.filter((row) => row.id !== id) },
      );
      return { previous };
    },
    onSuccess: () => {
      showToast({ tone: "success", message: "Failure mode deleted." });
    },
    onError: (error: unknown, _id, context) => {
      if (context?.previous != null) {
        queryClient.setQueryData(["quality", "failure-modes"], context.previous);
      }
      showToast({
        tone: "error",
        message: error instanceof ApiError ? error.message : "This failure mode is on an NCR and cannot be deleted.",
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["quality", "failure-modes"] });
    },
  });

  const columns = useMemo<HubDataTableColumn<FailureModeRow>[]>(() => {
    const scriptOptions = list.data?.scriptOptions ?? [];
    const categories = [...new Set((list.data?.rows ?? []).map((row) => row.category))];
    return [
      {
        id: "category",
        header: "Category",
        sortValue: (row) => row.category,
        cellClassName: "align-middle",
        cell: (row) => (
          <FailureModeCategorySelect
            value={row.category}
            categories={categories}
            disabled={!canEdit}
            feedbackId={`failure-mode-${row.id}-category`}
            onCommit={(category) => saveDetails.mutateAsync({ id: row.id, category })}
          />
        ),
      },
      {
        id: "name",
        header: "Failure mode",
        cellClassName: "align-middle",
        sortValue: (row) => row.name,
        cell: (row) => (
          <FailureModeShortText
            value={row.name}
            label={`Failure mode name for ${row.name}`}
            feedbackId={`failure-mode-${row.id}-name`}
            disabled={!canEdit}
            onCommit={(name) => saveDetails.mutateAsync({ id: row.id, name })}
          />
        ),
      },
      {
        id: "description",
        header: "Description",
        cellClassName: "h-px align-top",
        sortValue: (row) => row.description,
        cell: (row) => (
          <FailureModeLongText
            value={row.description}
            label={`Description for ${row.name}`}
            feedbackId={`failure-mode-${row.id}-description`}
            disabled={!canEdit}
            onCommit={(description) => saveDetails.mutateAsync({ id: row.id, description })}
          />
        ),
      },
      {
        id: "operatorScripts",
        header: "Operator scripts",
        cellClassName: "w-52 max-w-52 px-3 align-top",
        sortValue: (row) => row.operatorScripts.map((script) => script.label).join(", "),
        cell: (row) => (
          <FailureModeScriptSelect
            linked={row.operatorScripts}
            options={list.data?.operatorScripts ?? []}
            disabled={!canEdit}
            addLabel="Link a script"
            searchPlaceholder="Search operator scripts"
            emptyMessage="No operator scripts"
            onChange={(scriptIds) => saveOperatorLinks.mutate({ id: row.id, scriptIds })}
          />
        ),
      },
      {
        id: "scripts",
        header: "Tier 1 scripts",
        cellClassName: "w-52 max-w-52 px-3 align-top",
        sortValue: (row) => row.scripts.map((script) => script.label).join(", "),
        cell: (row) => (
          <FailureModeScriptSelect
            linked={row.scripts}
            options={scriptOptions}
            disabled={!canEdit}
            addLabel="Link a script"
            searchPlaceholder="Search tier 1 scripts"
            emptyMessage="No tier 1 scripts"
            onChange={(scriptIds) => saveScripts.mutate({ id: row.id, scriptIds })}
          />
        ),
      },
      {
        id: "ncrCount",
        header: "NCRs",
        align: "right",
        cellClassName: "align-middle",
        sortValue: (row) => row.ncrCount,
        cell: (row) => <span className="tabular-nums">{row.ncrCount}</span>,
      },
      {
        id: "delete",
        header: "",
        cellClassName: "align-middle",
        sortValue: () => "",
        cell: (row) =>
          canEdit ? (
            <button
              type="button"
              aria-label={`Delete ${row.name}`}
              title={row.ncrCount > 0 ? "This failure mode is on an NCR and cannot be deleted." : "Delete"}
              className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:text-slate-300"
              disabled={row.ncrCount > 0 || (removeMode.isPending && removeMode.variables === row.id)}
              onClick={(event) => {
                event.stopPropagation();
                if (row.ncrCount > 0) return;
                removeMode.mutate(row.id);
              }}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          ) : null,
      },
    ];
  }, [canEdit, list.data?.operatorScripts, list.data?.rows, list.data?.scriptOptions, removeMode, saveDetails, saveOperatorLinks, saveScripts]);

  return (
    <HubWorkspacePage>
      <HubWorkspaceHeader className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp
          breadcrumbLabel="Failure modes"
          hoverHint="Observable failure modes. Link operator scripts for immediate troubleshooting, and tier 1 scripts for review. A mode that is already on an NCR cannot be deleted."
        >
          Failure modes
        </PageTitleWithHelp>
        {canEdit ? (
          <Button type="button" onClick={() => setCreateOpen(true)}>
            Add new failure mode
          </Button>
        ) : null}
      </HubWorkspaceHeader>
      <HubWorkspaceBody className="overflow-hidden">
        <HubDataTable
          className="min-h-0 w-full flex-1 max-h-none lg:max-h-none"
          rows={list.data?.rows ?? []}
          columns={columns}
          rowKey={(row) => row.id}
          loading={list.isLoading}
          error={list.error instanceof Error ? list.error.message : null}
          empty="No failure modes."
          defaultSortKey="category"
          defaultSortDir="asc"
          keepRowOrderUntilResort
        />
      </HubWorkspaceBody>
      <CreateFailureModeModal
        open={createOpen}
        pending={createMode.isPending}
        categories={[...new Set((list.data?.rows ?? []).map((row) => row.category))].sort((left, right) => left.localeCompare(right))}
        onClose={() => {
          if (!createMode.isPending) setCreateOpen(false);
        }}
        onCreate={(input) => createMode.mutate(input)}
      />
    </HubWorkspacePage>
  );
}
